/**
 * GlobalAIButton — Sparkle icon in the app bar that opens a context-aware
 * AI assistant panel. Auto-detects the current module from the route and
 * passes the current page's entity context (from PageContext) so the AI
 * knows what entity the user may be talking about.
 *
 * Always provides tools for the current module. The AI interprets whether
 * the user is asking about the open entity, the module, or something general.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { AIChatSidePanel } from './AIChatSidePanel';
import { DemoLumaPanel } from './DemoLumaPanel';
import { isDemoMode } from '../../auth/demoSession';
import { CHAT_SESSION_PARAMS } from './chatSessionParams';
import { useChatUrlRouting } from '../../hooks/useChatUrlRouting';
import { useModule } from '../../contexts/ModuleContext';
import { useModuleName } from '../../hooks/useModuleName';
import { usePageContext } from '../../contexts/PageContext';
import { useChatLauncher } from '../../contexts/ChatLauncherContext';
import { getModuleAiMcpModules } from './mcpModuleMapping';

/** Per-module starter prompts shown as clickable buttons in the empty chat state. */
const SUGGESTED_PROMPTS_BY_MODULE: Record<string, string[]> = {
  inventory: [
    'aiChat:suggestedPrompts.inventory.reorder',
    'aiChat:suggestedPrompts.inventory.lowStock',
    'aiChat:suggestedPrompts.inventory.valuation',
  ],
};

export function GlobalAIButton({ moduleColor }: { moduleColor: string }) {
  const { t } = useTranslation(['aiChat', 'common']);
  const { currentModule } = useModule();
  const getModuleName = useModuleName();
  const pageContext = usePageContext();
  const { request: launchRequest, clearRequest } = useChatLauncher();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  /** Live copy of launcher-supplied entities; editable via chip × pre-send. */
  const [pendingAboutEntities, setPendingAboutEntities] = useState<
    Array<{ ref: string; label: string; type?: string }> | null
  >(null);
  const [launchTitle, setLaunchTitle] = useState<string | null>(null);
  const [launchPlaceholder, setLaunchPlaceholder] = useState<string | null>(null);
  /** Text to pre-fill the input with for a launched session; cleared once applied. */
  const [launchInitialInput, setLaunchInitialInput] = useState<string | null>(null);

  const location = useLocation();

  // Pages that host their own embedded chat manage `?chat=` themselves; on those
  // routes the global Luma overlay yields its param (binds to nothing) so the
  // two never render the same session side by side.
  const pageOwnsChatParam = /^\/ai-agents\/[^/]+/.test(location.pathname);

  // ── Session binding via the shared controller ──
  // The overlay binds to its OWN `?luma=` param (disjoint from page-embedded
  // `?chat=`). owned=false → URL plumbing only: the AIChatSidePanel below is the
  // useChatRouting controller, so routing here too would double-bind. `param=null`
  // on routes that own their chat disables binding entirely.
  const {
    sessionId: chatSessionId,
    setSessionId: handleChatSessionChange,
    isDeepLinked,
  } = useChatUrlRouting({
    param: pageOwnsChatParam ? null : CHAT_SESSION_PARAMS.globalLuma,
    owned: false,
  });

  // Auto-open the panel when deep-linked with a bound session (on routes that
  // own their own embedded chat, isDeepLinked is false — so the overlay stays
  // closed there).
  useEffect(() => {
    if (isDeepLinked && !open) {
      setOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

  // Consume launch request: open a fresh session with the supplied entities.
  useEffect(() => {
    if (!launchRequest) return;
    setPendingAboutEntities(launchRequest.aboutEntities);
    setLaunchTitle(launchRequest.title ?? null);
    setLaunchPlaceholder(launchRequest.inputPlaceholder ?? null);
    setLaunchInitialInput(launchRequest.initialMessage ?? null);
    // Clear any previously bound session — new attached entities mean new session.
    handleChatSessionChange(null);
    setOpen(true);
    clearRequest();
  }, [launchRequest, clearRequest, handleChatSessionChange]);

  const moduleId = currentModule.id;

  // Single source (mcpModuleMapping) for which UI modules have an in-app Luma
  // assistant and which backend MCP toolset(s) back each one. `master_data`
  // spans two (products + contacts); most map to a single module.
  const mcpModuleNames = useMemo(
    () => getModuleAiMcpModules(moduleId),
    [moduleId]
  );
  const hasModuleAI = mcpModuleNames.length > 0;
  // Primary MCP module — drives entityRef scoping, starter prompts, and the
  // post-generation query invalidation (the panel enables all of them).
  const mcpModuleName = mcpModuleNames[0] ?? moduleId;

  const moduleName = getModuleName(currentModule);

  // Always provide module tools when the module has AI
  const enabledModules = useMemo(() => {
    if (!hasModuleAI) return undefined;
    return mcpModuleNames;
  }, [hasModuleAI, mcpModuleNames]);

  const moduleAccess = useMemo(() => {
    if (!hasModuleAI) return undefined;
    return Object.fromEntries(
      mcpModuleNames.map((m) => [m, 'readwrite' as const])
    );
  }, [hasModuleAI, mcpModuleNames]);

  const lockedModules = useMemo(() => {
    if (!hasModuleAI) return undefined;
    return Object.fromEntries(
      mcpModuleNames.map((m) => [m, 'readwrite' as const])
    );
  }, [hasModuleAI, mcpModuleNames]);

  // Build entity ref: use the leaf entity from page context if available
  const entityRef = useMemo(() => {
    if (pageContext && pageContext.breadcrumbs.length > 0) {
      const leaf = pageContext.breadcrumbs[pageContext.breadcrumbs.length - 1];
      return `${leaf.type}:${leaf.id}`;
    }
    return `module:${hasModuleAI ? mcpModuleName : 'global'}`;
  }, [pageContext, hasModuleAI, mcpModuleName]);

  // Build context with page entity hierarchy or current page info.
  // Also extract flat entity ID keys (e.g., board_id, workspace_id) from
  // breadcrumbs so module chat providers can use them directly.
  const chatContext = useMemo(() => {
    if (pageContext && pageContext.breadcrumbs.length > 0) {
      // Extract flat entity IDs from breadcrumbs for module providers.
      // Maps breadcrumb type → context key: "board" → "board_id", "item" → "task_id", etc.
      const entityIds: Record<string, string> = {};
      for (const crumb of pageContext.breadcrumbs) {
        const key = crumb.type === 'item' ? 'task_id' : `${crumb.type}_id`;
        entityIds[key] = crumb.id;
      }

      return {
        ...entityIds,
        page_context: {
          module: pageContext.module,
          breadcrumbs: pageContext.breadcrumbs,
        },
      };
    }
    // List or other page — pass current page description
    // Derive a human-readable page name from the URL path
    const pathSegments = location.pathname.split('/').filter(Boolean);
    // e.g., /sales/deals → "Deals", /tasks/dashboard → "Dashboard"
    const lastSegment = pathSegments[pathSegments.length - 1] || '';
    // Skip UUIDs
    const isUuid = /^[0-9a-f]{8}-/.test(lastSegment);
    const pageName = isUuid
      ? pathSegments[pathSegments.length - 2] || moduleId
      : lastSegment;
    const readablePage = pageName
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      page_context: {
        module: moduleId,
        current_page: readablePage,
      },
    };
  }, [pageContext, location.pathname, moduleId]);

  const suggestedPrompts = useMemo(() => {
    const keys = SUGGESTED_PROMPTS_BY_MODULE[mcpModuleName];
    if (!keys) return undefined;
    return keys.map((key) => t(key));
  }, [mcpModuleName, t]);

  const panelTitle = hasModuleAI
    ? t('aiChat:globalAssistant.moduleMode', { module: moduleName })
    : t('aiChat:globalAssistant.globalMode');

  const inputPlaceholder = hasModuleAI
    ? t('aiChat:globalAssistant.askAboutModule', { module: moduleName })
    : t('aiChat:globalAssistant.askAnything');

  // ── Invalidate page data when AI finishes generating ──
  // Scope to the current module's queries plus chat sessions. A blanket
  // `invalidateQueries()` refetches everything in the app — including queries
  // whose re-renders can clobber shared URL state and racy side effects
  // (e.g., the `?chat=` param that keeps the AI panel bound to its session).
  const handleGenerationComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    // Invalidate every enabled MCP module's queries (master_data enables both
    // products and contacts), so any data Luma just changed refetches.
    for (const mod of mcpModuleNames) {
      queryClient.invalidateQueries({ queryKey: [mod] });
    }
  }, [queryClient, mcpModuleNames]);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            data-testid="global-ai-button"
            onClick={() => setOpen(true)}
            className="hover:bg-foreground/10"
            style={{ color: moduleColor }}
            aria-label={
              hasModuleAI
                ? t('aiChat:globalAssistant.tooltipModule', { module: moduleName })
                : t('aiChat:globalAssistant.tooltip')
            }
          >
            <Sparkles className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {hasModuleAI
            ? t('aiChat:globalAssistant.tooltipModule', { module: moduleName })
            : t('aiChat:globalAssistant.tooltip')}
        </TooltipContent>
      </Tooltip>
      {isDemoMode() ? (
        // Read-only demo: route Luma through the ephemeral one-shot path
        // (no persistent session written to the shared golden tenant).
        <DemoLumaPanel
          open={open}
          onClose={() => {
            setOpen(false);
            setPendingAboutEntities(null);
            setLaunchTitle(null);
            setLaunchPlaceholder(null);
            setLaunchInitialInput(null);
          }}
          title={launchTitle ?? panelTitle}
          inputPlaceholder={launchPlaceholder ?? inputPlaceholder}
          emptyMessage={t('aiChat:startConversation')}
          enabledModules={enabledModules}
        />
      ) : (
      <AIChatSidePanel
        open={open}
        onClose={() => {
          // Collapse only hides the panel — we deliberately KEEP the session
          // bound (do NOT clear the `?luma=` param). The observer WebSocket in
          // useChatSession lives outside the Sheet's collapsible content, so
          // leaving the session bound keeps it open: an in-flight turn keeps
          // streaming in the background and is fully resumed in the foreground
          // when the panel is re-opened. Clearing the session here would reset
          // useChatSession, tear down the WS, and drop the in-flight message.
          // (The dedicated `?luma=` param is why a left-bound session no longer
          // leaks into page-embedded `?chat=` chats — see CHAT_SESSION_PARAMS.)
          // Launcher-supplied (pre-send) overrides are still cleared.
          setOpen(false);
          setPendingAboutEntities(null);
          setLaunchTitle(null);
          setLaunchPlaceholder(null);
          setLaunchInitialInput(null);
        }}
        entityRef={entityRef}
        // eslint-disable-next-line i18next/no-literal-string -- 'Luma' is the AI assistant brand name, not translatable copy
        entityName={hasModuleAI ? moduleName : 'Luma'}
        title={launchTitle ?? panelTitle}
        inputPlaceholder={launchPlaceholder ?? inputPlaceholder}
        suggestedPrompts={suggestedPrompts}
        enabledModules={enabledModules}
        moduleAccess={moduleAccess}
        lockedModules={lockedModules}
        context={chatContext}
        chatSessionId={chatSessionId}
        onChatSessionChange={handleChatSessionChange}
        onGenerationComplete={handleGenerationComplete}
        aboutEntitiesOverride={pendingAboutEntities ?? undefined}
        onAboutEntitiesOverrideChange={(next) => {
          setPendingAboutEntities(next.length > 0 ? next : null);
        }}
        initialInput={launchInitialInput ?? undefined}
      />
      )}
    </>
  );
}
